/* =============================================================================
 * APS DESIGN TOKENS — TS mirror (portal / web)
 * -----------------------------------------------------------------------------
 * Typed token object for INLINE-STYLE consumption in React components, mirroring
 * `portal/src/styles/tokens.css` (CSS variables) 1:1. Prefer the CSS variables
 * (var(--aps-*)) in className/stylesheet contexts; use this object where a value
 * is needed in a JS `style={{}}` prop or computed at runtime.
 *
 * MUST stay in lockstep with tokens.css and mobile/src/styles/tokens.ts.
 * Semantics: success = earned/positive; action = orange CTA; warning = billing
 * failure ONLY; danger = destructive/error.
 * ========================================================================== */

export const color = {
  ink: '#151133',
  onInk: '#ffffff',

  action: '#f9550d',
  actionHover: '#c7440a',
  actionTint: '#fff7ed',
  actionTintStrong: '#fff3ed',
  actionBorder: '#fcd9c4',
  actionOnTint: '#b5390a',

  success: '#12b76a',
  successStrong: '#10b981',
  successTint: '#ecfdf3',
  successTint2: '#f0fbf6',
  successBorder: '#c6f0da',
  successBorderStrong: '#abefc6',
  successText: '#067647',

  warning: '#f59e0b', // BILLING FAILURE ONLY
  danger: '#dc2626',

  text: '#151133',
  text2: '#475467',
  textMuted: '#667085',
  textSubtle: '#98a2b3',

  border: '#eaecf0',
  border2: '#e4e7ec',
  borderStrong: '#d0d5dd',

  surface: '#ffffff',
  surface2: '#fbfcfd',
  page: '#f7f8fa',
  fill: '#f2f4f7',
  fill2: '#eff1f5',

  heartHigh: '#10b981',
  heartMed: '#f59e0b',
  heartLow: '#dc2626',

  // Added post-verification (drift-prevention)
  ink2: '#241a52',
  warningTint: '#fef3c7', // BILLING ONLY
  warningText: '#b5470a', // BILLING ONLY
  dangerTint: '#fef3f2',
  skeletonBase: '#eef0f3',
  skeletonSheen: '#f6f7f9',
  scrim: 'rgba(16, 24, 40, 0.5)',

  /* Form-control neutrals (KGH-795) — see tokens.css for why these exist.
   * The filter controls shipped on this ramp; naming them keeps the shared
   * filter primitives token-driven without moving athlete-side pixels. */
  controlLabel: '#374151',
  controlBorder: '#e5e7eb',
  controlDivider: '#f3f4f6',
  controlMuted: '#667085', // 4.97:1 on white (was #9ca3af, 2.54:1 - failed AA)
  controlTagBorder: '#fdba74',
  controlTagText: '#ea580c',
} as const;

export const radius = {
  sm: 8,
  md: 10,
  crest: 11,
  lg: 12,
  xl: 16,
  xxl: 18,
  xxxl: 24,
  pill: 999,
  bubble: 16,
  notch: 4,
} as const;

export const space = {
  1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 7: 28, 8: 32,
} as const;

export const font = {
  sans: "'Manrope', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  size: { caption: 12, labelSm: 13, label: 14, body: 15, heading: 16, title: 24, display: 40 },
  weight: { regular: 400, medium: 500, semibold: 600, bold: 700, heavy: 800 },
} as const;

export const elevation = {
  e1: '0 1px 2px rgba(16, 24, 40, 0.05)',
  e2: '0 4px 14px rgba(16, 24, 40, 0.08)',
  e3: '0 10px 30px rgba(16, 24, 40, 0.12)',
  pop: '0 20px 48px rgba(16, 24, 40, 0.18)',
} as const;

export const motion = {
  fast: '120ms',
  base: '240ms',
  slide: '320ms',
  count: '500ms',
  pulse: '600ms',
  easeOut: 'cubic-bezier(0.22, 1, 0.36, 1)',
} as const;

export const focusRing = '0 0 0 3px rgba(249, 85, 13, 0.35)';

export const tokens = { color, radius, space, font, elevation, motion, focusRing } as const;
export default tokens;
